/**
 * DataTableSkeleton Component
 *
 * Loading state skeleton for DataTable.
 */

import React from 'react';

interface DataTableSkeletonProps {
  columns: number;
  rows?: number;
  showCheckbox?: boolean;
  showExpand?: boolean;
  showActions?: boolean;
}

export function DataTableSkeleton({
  columns,
  rows = 5,
  showCheckbox = false,
  showExpand = false,
  showActions = false
}: DataTableSkeletonProps) {
  const totalColumns = columns
    + (showCheckbox ? 1 : 0)
    + (showExpand ? 1 : 0)
    + (showActions ? 1 : 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-pulse">
      <table className="w-full">
        {/* Header skeleton */}
        <thead className="bg-gray-50">
          <tr>
            {showExpand && (
              <th className="w-10 px-2 py-3">
                <div className="h-4 w-4 bg-gray-200 rounded" />
              </th>
            )}
            {showCheckbox && (
              <th className="w-12 px-4 py-3">
                <div className="h-4 w-4 bg-gray-200 rounded" />
              </th>
            )}
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-6 py-3">
                <div className="h-4 bg-gray-200 rounded w-24" />
              </th>
            ))}
            {showActions && (
              <th className="px-6 py-3 text-right">
                <div className="h-4 bg-gray-200 rounded w-16 ml-auto" />
              </th>
            )}
          </tr>
        </thead>

        {/* Body skeleton */}
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {showExpand && (
                <td className="w-10 px-2 py-4">
                  <div className="h-4 w-4 bg-gray-200 rounded" />
                </td>
              )}
              {showCheckbox && (
                <td className="w-12 px-4 py-4">
                  <div className="h-4 w-4 bg-gray-200 rounded" />
                </td>
              )}
              {Array.from({ length: columns }).map((_, colIndex) => (
                <td key={colIndex} className="px-6 py-4">
                  <div
                    className="h-4 bg-gray-200 rounded"
                    style={{ width: `${Math.random() * 40 + 60}%` }}
                  />
                </td>
              ))}
              {showActions && (
                <td className="px-6 py-4">
                  <div className="flex justify-end gap-2">
                    <div className="h-8 w-8 bg-gray-200 rounded" />
                    <div className="h-8 w-8 bg-gray-200 rounded" />
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination skeleton */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <div className="h-4 bg-gray-200 rounded w-48" />
        <div className="flex gap-2">
          <div className="h-8 w-8 bg-gray-200 rounded" />
          <div className="h-8 w-8 bg-gray-200 rounded" />
          <div className="h-8 w-8 bg-gray-200 rounded" />
          <div className="h-8 w-8 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );
}

export default DataTableSkeleton;
